/**
 * Helpers for policy-holder exclusion coverage selection.
 *
 * The policy-holder form consumes the nested company → insurance → policy →
 * group → coverage response returned by the active-coverages-with-dependents
 * endpoint.
 */

const coverageNode = (coverage, policy, group) => ({
  id: coverage.employeeCoverageId,
  coverage: {
    name: coverage.coverageName
  },
  dependents: coverage.dependents,
  policy: {
    id: policy.policyId,
    numberPolicy: policy.policyNumber,
    groupCompany: {
      id: group.groupId,
      name: group.groupName
    }
  }
});

const assertObject = (value, path) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`Invalid active coverage response at ${path}`);
  }
};

const assertArray = (value, path) => {
  if (!Array.isArray(value)) {
    throw new TypeError(`Invalid active coverage response at ${path}`);
  }
};

const assertField = (value, field, path, validator) => {
  if (
    !Object.prototype.hasOwnProperty.call(value, field) ||
    !validator(value[field])
  ) {
    throw new TypeError(`Invalid active coverage response at ${path}.${field}`);
  }
};

const isPositiveInteger = (value) => Number.isInteger(value) && value > 0;
const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0;

const assertId = (value, field, path) =>
  assertField(value, field, path, isPositiveInteger);

const assertName = (value, field, path) =>
  assertField(value, field, path, isNonEmptyString);

const validateCanonicalActiveCoverageResponse = (payload) => {
  assertArray(payload, 'payload');
  payload.forEach((company, companyIndex) => {
    const companyPath = `payload[${companyIndex}]`;
    assertObject(company, companyPath);
    assertId(company, 'companyId', companyPath);
    assertName(company, 'companyName', companyPath);
    assertArray(company.insurances, `${companyPath}.insurances`);

    company.insurances.forEach((insurance, insuranceIndex) => {
      const insurancePath = `${companyPath}.insurances[${insuranceIndex}]`;
      assertObject(insurance, insurancePath);
      assertId(insurance, 'insuranceCompanyId', insurancePath);
      assertName(insurance, 'insuranceCompanyName', insurancePath);
      assertArray(insurance.policies, `${insurancePath}.policies`);

      insurance.policies.forEach((policy, policyIndex) => {
        const policyPath = `${insurancePath}.policies[${policyIndex}]`;
        assertObject(policy, policyPath);
        assertId(policy, 'policyId', policyPath);
        assertName(policy, 'policyNumber', policyPath);
        assertArray(policy.groups, `${policyPath}.groups`);

        policy.groups.forEach((group, groupIndex) => {
          const groupPath = `${policyPath}.groups[${groupIndex}]`;
          assertObject(group, groupPath);
          assertId(group, 'groupId', groupPath);
          assertName(group, 'groupName', groupPath);
          assertArray(group.coverages, `${groupPath}.coverages`);

          group.coverages.forEach((coverage, coverageIndex) => {
            const coveragePath = `${groupPath}.coverages[${coverageIndex}]`;
            assertObject(coverage, coveragePath);
            assertId(coverage, 'employeeCoverageId', coveragePath);
            assertName(coverage, 'coverageName', coveragePath);
            assertArray(coverage.dependents, `${coveragePath}.dependents`);

            coverage.dependents.forEach((dependent, dependentIndex) => {
              const dependentPath = `${coveragePath}.dependents[${dependentIndex}]`;
              assertObject(dependent, dependentPath);
              assertId(dependent, 'dependentId', dependentPath);
              assertName(dependent, 'fullName', dependentPath);
              assertName(dependent, 'rut', dependentPath);
            });
          });
        });
      });
    });
  });
};

/**
 * Normalize the active-coverages-with-dependents nested response to the tree
 * consumed by the policy-holder form.
 */
export const normalizeActiveCoverageTree = (payload) => {
  validateCanonicalActiveCoverageResponse(payload);

  return payload.map((company) => ({
    id: company.companyId,
    businessName: company.companyName,
    insurances: company.insurances.map((insurance) => ({
      id: insurance.insuranceCompanyId,
      businessName: insurance.insuranceCompanyName,
      policies: insurance.policies.map((policy) => ({
        id: policy.policyId,
        numberPolicy: policy.policyNumber,
        insuranceBrokerId: policy.insuranceBrokerId,
        groupCompanies: policy.groups.map((group) => ({
          id: group.groupId,
          name: group.groupName,
          coverages: group.coverages.map((coverage) =>
            coverageNode(coverage, policy, group)
          )
        }))
      }))
    }))
  }));
};

export const flattenActiveCoverageTree = (payload) =>
  normalizeActiveCoverageTree(payload).flatMap((company) =>
    company.insurances.flatMap((insurance) =>
      insurance.policies.flatMap((policy) =>
        policy.groupCompanies.flatMap((group) =>
          group.coverages.map((coverage) => ({
            id: coverage.id,
            dependents: coverage.dependents
          }))
        )
      )
    )
  );

export const getActiveDependents = (coverage) => {
  if (!Array.isArray(coverage?.dependents)) return [];
  return coverage.dependents.filter(
    (dependent) =>
      !dependent?.status ||
      dependent.status === 'ACTIVE' ||
      dependent.status === 'active'
  );
};

export const getCoverageSelectionOptions = (tree, selectedPolicyIds = []) => {
  const policyIds = new Set(selectedPolicyIds.map((id) => Number(id)));

  return (Array.isArray(tree) ? tree : [])
    .flatMap((company) => company.insurances || [])
    .flatMap((insurance) => insurance.policies || [])
    .filter((policy) => policyIds.has(Number(policy.id)))
    .flatMap((policy) =>
      (policy.groupCompanies || []).flatMap((group) =>
        (group.coverages || []).map((coverage) => ({
          value: coverage.id,
          label: `${policy.numberPolicy} · ${group.name} · ${coverage.coverage.name}`,
          coverage,
          policy,
          group
        }))
      )
    );
};

export const getSelectedCoverageDependents = (options, selectedIds) => {
  const selected = new Set((selectedIds || []).map((id) => Number(id)));

  return (Array.isArray(options) ? options : [])
    .filter((option) => selected.has(Number(option.value)))
    .map((option) => ({
      ...option,
      dependents: getActiveDependents(option.coverage)
    }));
};

export const selectAllCoverageIds = (options) =>
  (Array.isArray(options) ? options : []).map((option) => option.value);
